import { Button, Checkbox, Popover, Select, Space, Tag, message } from "antd";
import axios from "axios";
import Cookies from "js-cookie";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Slider from "react-slick";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";

import fallbackImage from "../../assets/images/shipping/blank_images.png";
import { refresh } from "../../store/adminSlice";
import ImageWithSpinner from "../Shipping/ImageWithSpinner";
import OrderSkeleton from "./OrderSkeleton";

export default function Orders1688() {
  const { t } = useTranslation();
  const [orderIds, setOrderIds] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [orderDetails, setOrderDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [failedImages, setFailedImages] = useState({});
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [creatingShipping, setCreatingShipping] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Add custom styles for slider dots
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      .custom-dots {
        margin-top: 0 !important;
        margin-bottom: 0 !important;
        
      }
      .custom-dots li {
        margin: 0 4px;
      }
      .custom-dots li button:before {
        font-size: 8px;
        color: #ccc;
        opacity: 0.8;
      }
      .custom-dots li.slick-active button:before {
        color:rgb(143, 179, 214);
        opacity: 1;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const fetchOrderIds = async () => {
    try {
      const response = await axios.get("order/api/v1/bulk-orders/admin/");
      const sortedOrderIds = response.data.sort((a, b) => a - b);
      setOrderIds(sortedOrderIds);
    } catch (err) {
      if (err.response?.status === 401 && Cookies.get("logss")) {
        dispatch(refresh()).then(() => fetchOrderIds());
      } else if (err.response?.status === 401 && !Cookies.get("logss")) {
        navigate("/");
      } else {
        message.error(t("orders1688.toast.fetchOrderIdsError"));
      }
    }
  };

  const handleOrderSelect = async (value) => {
    setSelectedOrderId(value);
    setLoading(true);
    setOrderDetails([]);
    try {
      const response = await axios.get(
        `/shipping/api/v1/alibaba-orders/${value}/`,
      );
      setOrderDetails(response.data);
    } catch (err) {
      if (err.response?.status === 401 && Cookies.get("logss")) {
        dispatch(refresh()).then(() => handleOrderSelect(value));
      } else if (err.response?.status === 401 && !Cookies.get("logss")) {
        navigate("/");
      } else {
        message.error(t("orders1688.toast.fetchOrderDetailsError"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMarkArrived = async (orderId) => {
    try {
      await axios.post(`/alibaba/api/v1/mark-arrived/${orderId}/`);
      message.success(
        t("orders1688.toast.markedArrived", "Order marked as arrived"),
      );
      handleOrderSelect(selectedOrderId);
    } catch (err) {
      message.error(
        err.response?.data?.message ||
          t(
            "orders1688.toast.markArrivedFailed",
            "Failed to mark order as arrived",
          ),
      );
    }
  };

  // Handle image error by trying the next image in the array
  const handleImageError = (itemId, imageIndex, item) => {
    const imagesArray = item.images ? item.images.split(",") : [];

    // If this is the last image in the array or there are no additional images, use fallback
    if (!imagesArray.length || imageIndex >= imagesArray.length - 1) {
      setFailedImages((prev) => ({
        ...prev,
        [itemId]: -1, // -1 indicates to use fallback
      }));
      return;
    }

    // Try the next image in the array
    setFailedImages((prev) => ({
      ...prev,
      [itemId]: (prev[itemId] || 0) + 1,
    }));
  };

  // Get the current image source for an item
  const getImageSource = (item) => {
    if (failedImages[item.id] === -1) {
      return fallbackImage;
    }

    const imagesArray = item.images ? item.images.split(",") : [];
    const imageToUse =
      failedImages[item.id] !== undefined && failedImages[item.id] >= 0
        ? imagesArray[failedImages[item.id]]
        : item.image;

    return `${process.env.REACT_APP_PUBLIC_MEDIA_URL}store/${item.store}/${imageToUse}`;
  };

  useEffect(() => {
    fetchOrderIds();
  }, []);

  const getTruncated = (value, max = 14) => {
    if (!value) return "";
    return value.length > max ? value.slice(0, max) + "…" : value;
  };

  const renderOrderItems = (items) => {
    if (!items) return null;

    return (
      <Slider
        dots={true}
        infinite={false}
        speed={500}
        slidesToShow={1}
        slidesToScroll={1}
        dotsClass="slick-dots custom-dots"
        appendDots={(dots) => (
          <div
            style={{
              position: "absolute",
              bottom: "-15px",
              display: "block",
              width: "100%",
            }}
          >
            <ul style={{ margin: "0", padding: "0" }}>{dots}</ul>
          </div>
        )}
      >
        {items.map((item) => {
          const storeTruncated = getTruncated(item.store, 14);
          const showStorePopover = item.store && item.store.length > 14;
          return (
            <div key={item.id} className="slider-item">
              <div
                className="order-detail-item"
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "flex-start",
                  gap: "10px",
                  padding: "3px",
                  backgroundColor: "#fafafa",
                  borderRadius: "6px",
                }}
              >
                <div
                  className="image-container"
                  style={{
                    width: "38%",
                    minWidth: "100px",
                    maxWidth: "180px",
                    flexShrink: 0,
                    position: "relative",
                  }}
                >
                  <ImageWithSpinner
                    item={item}
                    getImageSource={getImageSource}
                    handleImageError={handleImageError}
                    failedImages={failedImages}
                  />
                </div>
                <div
                  className="item-details"
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                  }}
                >
                  <p style={{ fontSize: "14px", marginBottom: "4px" }}>
                    <strong>{t("orders1688.order.product")}:</strong>{" "}
                    <Popover
                      content={item.product_name}
                      title={t("orders1688.order.product")}
                    >
                      <a
                        href={item.external_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#1890ff",
                          textDecoration: "none",
                          borderBottom:
                            item.product_name.length > 20
                              ? "1px dashed #999"
                              : "none",
                        }}
                      >
                        {item.product_name.length > 20
                          ? `${item.product_name.substring(0, 15)}...`
                          : item.product_name}
                      </a>
                    </Popover>
                  </p>
                  <p style={{ fontSize: "13px", marginBottom: "6px" }}>
                    <strong>{t("orders1688.order.store")}:</strong>{" "}
                    <Popover
                      content={item.store}
                      title={t("orders1688.order.store")}
                    >
                      <span
                        style={{
                          cursor: "pointer",
                          borderBottom:
                            item.store.length > 15 ? "1px dashed #999" : "none",
                        }}
                      >
                        {item.store.length > 15
                          ? `${item.store.substring(0, 15)}...`
                          : item.store}
                      </span>
                    </Popover>
                  </p>
                  <p style={{ fontSize: "13px", marginBottom: "6px" }}>
                    <strong>{t("orders1688.order.quantity")}:</strong>{" "}
                    {item.quantity}
                  </p>
                  {item.color && (
                    <p style={{ fontSize: "12px", marginBottom: "3px" }}>
                      <strong>{t("orders1688.order.color")}:</strong>{" "}
                      <Popover
                        content={item.color}
                        title={t("orders1688.order.color")}
                      >
                        <span
                          style={{
                            cursor: "pointer",
                            borderBottom:
                              item.color.length > 15
                                ? "1px dashed #999"
                                : "none",
                          }}
                        >
                          {item.color.length > 15
                            ? `${item.color.substring(0, 15)}...`
                            : item.color}
                        </span>
                      </Popover>
                    </p>
                  )}
                  {item.size && (
                    <p style={{ fontSize: "12px", marginBottom: "3px" }}>
                      <strong>{t("orders1688.order.size")}:</strong>{" "}
                      <Popover
                        content={item.size}
                        title={t("orders1688.order.size")}
                      >
                        <span
                          style={{
                            cursor: "pointer",
                            borderBottom:
                              item.size.length > 15
                                ? "1px dashed #999"
                                : "none",
                          }}
                        >
                          {item.size.length > 15
                            ? `${item.size.substring(0, 15)}...`
                            : item.size}
                        </span>
                      </Popover>
                    </p>
                  )}
                  <div style={{ marginTop: "4px" }}>
                    <Popover
                      content={
                        <div style={{ maxWidth: "300px" }}>
                          {item.category.map((cat, index) => (
                            <Tag
                              key={index}
                              color="blue"
                              style={{ margin: "2px" }}
                            >
                              {cat}
                            </Tag>
                          ))}
                        </div>
                      }
                      title={t("orders1688.order.categories")}
                    >
                      <Space wrap style={{ cursor: "pointer" }}>
                        {item.category.slice(0, 2).map((cat, index) => (
                          <Tag key={index} color="blue">
                            {cat}
                          </Tag>
                        ))}
                        {item.category.length > 2 && (
                          <Tag color="blue">+{item.category.length - 2}</Tag>
                        )}
                      </Space>
                    </Popover>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </Slider>
    );
  };

  const handleCheckboxChange = (orderId, shippingOrder) => {
    if (shippingOrder === null) {
      setSelectedOrders((prev) => {
        if (prev.includes(orderId)) {
          return prev.filter((id) => id !== orderId);
        } else {
          return [...prev, orderId];
        }
      });
    }
  };

  const renderOrder = (order) => (
    <div
      key={order.id}
      className="order-section"
      style={{
        backgroundColor: order.status === "canceled" ? "#ff09" : "#fff",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        overflow: "hidden",
        marginBottom: "16px",
      }}
    >
      <div
        className="order-summary"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "12px",
          borderBottom: "1px solid #eee",
        }}
      >
        <div>
          <h4 style={{ margin: 0 }}>
            {t("orders1688.order.id", { id: order.id })}
          </h4>
          <p style={{ margin: "4px 0" }}>
            <strong>{t("orders1688.order.trackingNumber")}:</strong>{" "}
            {order.tracking_number || " "}
          </p>
          <p style={{ margin: "4px 0" }}>
            <strong>{t("orders1688.order.status")}:</strong>{" "}
            <Tag color={order.status === "Arrived" ? "green" : "orange"}>
              {order.status}
            </Tag>
          </p>
          {order.shipping_order && (
            <p style={{ margin: "4px 0" }}>
              <strong>{t("orders1688.order.shippingOrder")}:</strong>{" "}
              <Tag color="blue">#{order.shipping_order}</Tag>
            </p>
          )}
        </div>
        <div className="d-flex align-items-center gap-2">
          <Popover
            content={t("orders1688.order.shippingOrderAssigned", {
              id: order.shipping_order,
            })}
            trigger="hover"
            placement="left"
          >
            <div style={{ position: "relative" }}>
              <Checkbox
                checked={selectedOrders.includes(order.id)}
                onChange={() =>
                  handleCheckboxChange(order.id, order.shipping_order)
                }
                disabled={order.shipping_order !== null}
                style={{
                  cursor:
                    order.shipping_order === null ? "pointer" : "not-allowed",
                  opacity: order.shipping_order === null ? 1 : 0.5,
                  position: "relative",
                  "&:hover": {
                    opacity: order.shipping_order === null ? 1 : 0.7,
                  },
                }}
              />
              {order.shipping_order !== null && (
                <div
                  style={{
                    position: "absolute",
                    top: "-2px",
                    right: "-2px",
                    width: "8px",
                    height: "8px",
                    backgroundColor: "#ff4d4f",
                    borderRadius: "50%",
                    border: "1px solid #fff",
                  }}
                />
              )}
            </div>
          </Popover>
        </div>
      </div>

      {order.order_items && order.order_items.length > 0 && (
        <div
          className="slider-container"
          style={{ padding: "10px", position: "relative" }}
        >
          {renderOrderItems(order.order_items)}
        </div>
      )}
    </div>
  );

  const handleCreateShipping = async () => {
    if (selectedOrders.length === 0) {
      message.warning(t("orders1688.toast.selectOrderWarning"));
      return;
    }

    setCreatingShipping(true);
    try {
      const response = await axios.post("/shipping/api/v1/shipping/create/", {
        alibaba_order_ids: selectedOrders,
      });

      if (response.status === 201) {
        message.success(t("orders1688.toast.createShippingSuccess"));
        handleOrderSelect(selectedOrderId);
        setSelectedOrders([]);
      }
    } catch (err) {
      if (err.response?.status === 400) {
        if (err.response.data.order_ids) {
          message.error(
            `${err.response.data.message}: ${err.response.data.order_ids.join(", ")}`,
          );
        } else {
          message.error(err.response.data.message);
        }
      } else if (err.response?.status === 404) {
        message.error(err.response.data.message);
      } else {
        message.error(t("orders1688.toast.createShippingError"));
      }
    } finally {
      setCreatingShipping(false);
    }
  };

  return (
    <div style={{ padding: "20px" }}>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", gap: "16px" }}>
            <Select
              style={{ width: 200 }}
              placeholder={t("orders1688.selectOrder")}
              onChange={handleOrderSelect}
              loading={loading}
              size="large"
              showSearch
              optionFilterProp="label"
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
              options={orderIds.map((id) => ({
                value: id,
                label: `${id}`,
              }))}
            />
          </div>
          {selectedOrders.length > 0 && (
            <Button
              type="primary"
              onClick={handleCreateShipping}
              loading={creatingShipping}
              danger
              style={{
                backgroundColor: "#ff4d4f",
                borderColor: "#ff4d4f",
                boxShadow: "0 2px 0 rgba(255, 77, 79, 0.1)",
                fontWeight: "bold",
                fontSize: "14px",
                height: "36px",
                padding: "0 20px",
                transition: "all 0.3s",
                "&:hover": {
                  backgroundColor: "#ff7875",
                  borderColor: "#ff7875",
                  transform: "translateY(-1px)",
                  boxShadow: "0 4px 8px rgba(255, 77, 79, 0.2)",
                },
                "&:active": {
                  backgroundColor: "#d9363e",
                  borderColor: "#d9363e",
                  transform: "translateY(0)",
                },
              }}
            >
              {t("orders1688.createShipping")} ({selectedOrders.length})
            </Button>
          )}
        </div>

        {loading ? (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "8px",
            }}
          >
            {[1, 2, 3].map((index) => (
              <OrderSkeleton key={index} />
            ))}
          </div>
        ) : (
          orderDetails && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "8px",
              }}
            >
              {Array.isArray(orderDetails) && orderDetails.length > 0 ? (
                orderDetails.map(renderOrder)
              ) : !Array.isArray(orderDetails) && orderDetails ? (
                renderOrder(orderDetails)
              ) : (
                <div
                  style={{
                    gridColumn: "1 / -1",
                    textAlign: "center",
                    padding: "20px",
                    backgroundColor: "#fff",
                    borderRadius: "8px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  }}
                >
                  <p style={{ margin: 0, fontSize: "16px", color: "#666" }}>
                    {t("orders1688.noData")}
                  </p>
                </div>
              )}
            </div>
          )
        )}
      </Space>
    </div>
  );
}
